import * as React from 'react';

import { useIntl } from 'react-intl';

import { PERMISSION } from 'src/features/auth';
import { commonMessages } from 'src/features/auth/utils';
import { useGetParking } from 'src/features/parkingManagement/api/useGetParking';
import { useListMemberships } from 'src/features/userManagement/api/useListMemberships';
import { useRetrieveMembershipValidator } from 'src/features/userManagement/api/useRetrieveMembershipValidator';
import { InvitationModalForm } from 'src/features/userManagement/components/forms';
import { EditUserModalForm } from 'src/features/userManagement/components/forms/EditUserModalForm.';
import UserStatusChangeModal from 'src/features/userManagement/components/forms/UserStatusChangeModal';
import { type Membership, MEMBERSHIP_ROLES } from 'src/features/userManagement/types';
import { userManagementMessages } from 'src/features/userManagement/utils';
import { getUsersTableColumns } from 'src/features/userManagement/utils/getUsersTableColumns';
import useFilters from 'src/hooks/useFilters';
import useHasPermissions from 'src/hooks/useHasPermissions';
import usePagination from 'src/hooks/usePagination';
import useSearch from 'src/hooks/useSearch';
import useSorting from 'src/hooks/useSorting';
import { useParking } from 'src/providers/ParkingProvider';
import { DataTable, DataTableControls, DataTablePagination } from 'src/registry/components/table';
import { generalMessages } from 'src/utils/messages';

const DeactivatedUsersTable = () => {
    const { formatMessage } = useIntl();
    const permissionViewStaff = useHasPermissions([PERMISSION.USERS_VIEW_STAFF]);
    const permissionInvitationManage = useHasPermissions([PERMISSION.USERS_INVITATION_MANAGE]);

    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [selectedMembership, setSelectedMembership] = React.useState<Membership | null>(null);
    const [isStatusModalOpen, setIsStatusModalOpen] = React.useState(false);

    const openEditMembershipModal = (member: Membership) => {
        setSelectedMembership(member);
        setIsModalOpen(true);
    };

    const openMembershipStatusModal = (member: Membership) => {
        setSelectedMembership(member);
        setIsStatusModalOpen(true);
    };

    const { parkingId } = useParking();
    const allowedRoles: MEMBERSHIP_ROLES[] = useGetParking({ parkingId }).data?.roles || [];
    const columns = getUsersTableColumns({
        openEditMembershipModal,
        openMembershipStatusModal,
        userStatusAction: 'activate',
        formatMessage,
        allowedRoles,
    });

    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySortingOptions, ...sorting } = useSorting();
    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });

    const { data, isLoading, isError, refetch } = useListMemberships({
        parkingId,
        user__is_staff: permissionViewStaff ? undefined : false,
        status: 'inactive',
        role: Object.values(MEMBERSHIP_ROLES).filter((role) => role !== MEMBERSHIP_ROLES.CLIENT),
        ...queryPaginationOptions,
        ...querySortingOptions,
        ...queryFiltersOptions,
        ...querySearchOptions,
        options: { enabled: !!parkingId },
    });

    const { data: membershipValidatorSettings } = useRetrieveMembershipValidator({
        parkingId,
        membershipId: selectedMembership?.id || '',
    });

    return (
        <>
            {isModalOpen && selectedMembership && (
                <EditUserModalForm
                    setIsModalOpen={setIsModalOpen}
                    membershipData={selectedMembership}
                    validatorSettings={membershipValidatorSettings}
                />
            )}
            {isStatusModalOpen && selectedMembership && (
                <UserStatusChangeModal
                    isUserStatusChangeModalOpen={isStatusModalOpen}
                    closeUserStatusChangeModal={() => setIsStatusModalOpen(false)}
                    selectedMember={selectedMembership}
                    statusAction="activate"
                    refetch={refetch}
                />
            )}
            <DataTable
                columns={columns}
                data={data}
                pagination={pagination}
                sorting={sorting}
                filters={filters}
                search={search}
                semantics={{
                    title: formatMessage(userManagementMessages.deactivated),
                    entryName: formatMessage(userManagementMessages.user),
                    entryNames: formatMessage(userManagementMessages.users),
                    placeholder: `${formatMessage(generalMessages.name)}, ${formatMessage(commonMessages.email)}`,
                }}
                isLoading={isLoading}
                isError={isError}
                refetch={refetch}
                components={{
                    controls: DataTableControls,
                    pagination: DataTablePagination,
                    // @ts-expect-error Not sure what the problem is here
                    newEntry: permissionInvitationManage ? InvitationModalForm : undefined,
                }}
            />
        </>
    );
};

export { DeactivatedUsersTable };
